import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/Auth/AppModule'
import Role from '@/models/Settings/Roles/Role';
import CreateUpdateRoleRequest from '@/models/Settings/Roles/CreateUpdateRoleRequest';
import PermissionUtils from '@/lib/permissionUtils';

export interface IRolesState {
    loading: boolean;
    loadingDialog: boolean;
    roles: Role[];
    totalRolesPages: number;
    role: Role;
    newRoleId: string;
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'RolesModule', namespaced: true })
class RolesStore extends VuexModule implements IRolesState {

    public loading: boolean = true;
    public loadingDialog: boolean = false;
    public roles: Role[] = [];
    public totalRolesPages: number = 1;
    public role: Role = new Role();
    public newRoleId: string = null;
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async createRole(request: CreateUpdateRoleRequest) {

        this.setLoadingDialog(true);
        this.context.commit("setRoleFromCreate", null);

        await ajax.post(`/portal-backend/fi-api/roles`, request)
        .then(response => {
            this.context.commit("setRoleFromCreate", response.data.id);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editRole(request: CreateUpdateRoleRequest) {

        this.setLoadingDialog(true);

        await ajax.put(`/portal-backend/fi-api/roles/${this.role.id}`, request)
        .then(response => {
            this.context.commit("setRole", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getRoles() {

        this.setLoading(true);

        await ajax.get('/portal-backend/fi-api/roles')
        .then(response => {
            this.context.commit("setRoles", response.data.results);
            // There are no "records" in the response in this case, so we have to calculate the length of
            // results manually, but make sure to handle the case when no roles are set
            if (response.data.results) {
                this.context.commit("setRolesTotalPages", response.data.results.length);
            }
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getRole(id: string) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/roles/${id}`)
        .then(response => {
            this.context.commit("setRole", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async deleteRole() {

        this.setLoading(true);

        await ajax.delete(`/portal-backend/fi-api/roles/${this.role.id}`)
        .then(() => {
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.loadingDialog = false;
        this.roles = [];
        this.totalRolesPages = 1;
        this.role = new Role();
        this.newRoleId = null;
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingDialog(loading: boolean) {
        this.loadingDialog = loading;
    }

    @Mutation
    private async setRoles(response: Role[]) {
        if (response) {
            this.roles = response;
        }
    }

    @Mutation
    private async setRolesTotalPages(total: number) {
        this.totalRolesPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setRole(response: Role) {
        this.role = response;
    }

    @Mutation
    private async setRoleFromCreate(roleId: string) {
        this.newRoleId = roleId;
    }

}

export const RolesModule = getModule(RolesStore);